import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut } from "lucide-react";
import rivyouLogo from "../assets/Rivyou_idjI_O9kJC_1.svg";
import { useAuth } from "../contexts/AuthContext";

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "trust-problem", label: "Internet's Trust Problem", path: "/the-badge/the-problem" },
  { id: "trust-issue", label: "Why Trust Us?" },
  { id: "the-badge", label: "The Badge", path: "/the-badge/things-to-know" },
  { id: "brands", label: "Brands" },
  { id: "for-businesses", label: "For Business" },
];

export default function Navbar({
  titleRef,
  titleTransform,
  titleTransition,
  uiRevealed,
  onReplay,
  onNavigate,
  activePage,
  searchTerm = "",
  selectedCategory = "All Brands",
  onlyVerified = true,
  onOpenFilterModal,
  onFocusSearch,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSignedIn, signIn, signOut, hasIncompleteOnboarding } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  // Close mobile menu on Escape key press or desktop viewport resize
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 900) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      const diff = currentScrollY - lastScrollYRef.current;

      if (diff > 8) {
        setIsNavVisible(false);
      } else if (diff < -8) {
        setIsNavVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobileMenuOpen, activePage]);

  const handleNavClick = (page) => {
    setIsMobileMenuOpen(false);
    if (page === "for-businesses" || page === "for-business") {
      if (window.innerWidth <= 768) {
        navigate("/business");
        return;
      }
      window.open("/business", "_blank", "noopener,noreferrer");
      return;
    }
    if (onNavigate) {
      onNavigate(page);
    }
  };

  // Lock body scroll and handle Escape key when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") setIsMobileMenuOpen(false);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isMobileMenuOpen]);

  const isHiddenByScroll = !isNavVisible && uiRevealed && !isMobileMenuOpen;

  return (
    <>
      {/* Mobile Menu Backdrop at root level to properly cover entire viewport */}
      {isMobileMenuOpen && (
        <div
          className="consumer-nav-backdrop fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] md:hidden pointer-events-auto animate-in fade-in duration-200"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        className={`consumer-nav-shell sticky top-0 md:top-3 z-[9999] w-full flex justify-center px-0 md:px-6 transition-[transform,opacity] duration-300 pointer-events-none ${
          isHiddenByScroll ? "-translate-y-full md:-translate-y-24 opacity-0" : "opacity-100"
        }`}
      >
        <header
          className={`consumer-nav-header pointer-events-auto relative z-50 w-full flex items-center justify-between transition-[background-color,border-color,box-shadow,max-width,padding] duration-300 box-border
            h-14 sm:h-15 rounded-none px-4
            md:max-w-[1280px] md:rounded-full md:px-6 md:py-2
            ${
              uiRevealed || isMobileMenuOpen
                ? "bg-white border-b border-brand-border md:bg-brand-subtle/95 md:backdrop-blur-md md:border md:border-brand-border md:shadow-xs"
                : "bg-transparent border-transparent shadow-none"
            }
          `}
        >
          {/* Left: Mobile Hamburger Trigger + Brand Logo */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Mobile Hamburger Button on the Left */}
            <button
              type="button"
              className={`consumer-nav-toggle flex md:hidden w-11 h-11 min-w-[44px] min-h-[44px] items-center justify-center rounded-xl text-brand-obsidian hover:bg-brand-border-subtle active:bg-brand-border transition-[color,background-color,opacity] duration-300 bg-transparent border-none cursor-pointer p-0 -ml-1 sm:-ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald ${
                uiRevealed ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation-menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-brand-obsidian" strokeWidth={2.5} />
              ) : (
                <Menu className="w-6 h-6 text-brand-obsidian" strokeWidth={2.5} />
              )}
            </button>

            {/* Brand Logo Anchor */}
            <button
              type="button"
              className="relative inline-flex items-center cursor-pointer bg-transparent border-0 p-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
              onClick={() => {
                setIsMobileMenuOpen(false);
                // activePage falls back to 'home' on pages like /privacy, so check the real path too
                if ((activePage !== "home" || location.pathname !== "/") && onNavigate) {
                  onNavigate("home");
                } else {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              title="Rivyou home"
              aria-label="Rivyou home"
            >
              <span
                ref={titleRef}
                className="inline-flex items-center justify-center w-20 h-7 select-none transition-opacity hover:opacity-85"
                style={{
                  transform: titleTransform,
                  transition: titleTransition,
                  position: "relative",
                  zIndex: 10000,
                }}
              >
                <img
                  src={rivyouLogo}
                  alt="Rivyou"
                  width="80"
                  height="28"
                  className="block w-20 h-7 object-contain pointer-events-none"
                />
              </span>
            </button>
          </div>

          {/* Center: Standard Desktop Nav Links */}
          <nav
            aria-label="Main Navigation"
            className={`consumer-nav-desktop-links hidden md:flex md:absolute md:left-1/2 md:-translate-x-1/2 items-center gap-0.5 xl:gap-2 2xl:gap-3 transition-opacity duration-300 ${uiRevealed ? "opacity-100" : "opacity-0"}`}
          >
            {NAV_ITEMS.map((tab) => {
              const isActive = activePage === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`px-2.5 xl:px-4 py-2 rounded-full text-[12.5px] xl:text-[13px] whitespace-nowrap transition-all border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald ${
                    isActive
                      ? "text-brand-emerald-hover font-bold bg-brand-border"
                      : "text-brand-forest font-medium bg-transparent hover:text-brand-emerald-hover hover:bg-brand-border-subtle"
                  }`}
                  onClick={() => handleNavClick(tab.path || tab.id)}
                  aria-current={isActive ? "page" : undefined}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Desktop Right Action Buttons */}
          <div
            className={`consumer-nav-desktop-actions hidden md:flex items-center gap-2.5 transition-opacity duration-300 ${uiRevealed ? "opacity-100" : "opacity-0"}`}
          >
            {isSignedIn ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="w-9 h-9 rounded-full bg-brand-emerald text-white hover:bg-brand-emerald-hover flex items-center justify-center font-bold text-sm shadow-xs border border-white/20 transition-transform hover:scale-105 cursor-pointer select-none overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
                  title={user?.name ? `${user.name} - View Profile` : "View Profile"}
                  aria-label="View Profile"
                >
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (user?.name?.trim()
                      ? user.name.trim()[0]
                      : user?.phone_number
                        ? user.phone_number.slice(-2)
                        : "U"
                    ).toUpperCase()
                  )}
                </button>
                {hasIncompleteOnboarding && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white ring-1 ring-amber-600/20"
                    title="Complete profile setup"
                    aria-label="Incomplete profile setup"
                  />
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => signIn({ returnTo: window.location.pathname })}
                className="nav-cta-btn bg-brand-emerald hover:bg-brand-emerald-hover text-white text-sm font-bold px-5 py-2 min-h-[38px] rounded-full border-0 cursor-pointer transition-transform hover:-translate-y-0.5 shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
              >
                Log in
              </button>
            )}
          </div>

          {/* Mobile Right Controls: Profile Avatar or Log In */}
          <div
            className={`consumer-nav-mobile-actions flex md:hidden items-center absolute right-3.5 top-1/2 -translate-y-1/2 z-50 transition-opacity duration-300 ${
              uiRevealed ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            {isSignedIn ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => navigate("/profile")}
                  className="w-9 h-9 min-w-[36px] min-h-[36px] rounded-full bg-brand-emerald text-white hover:bg-brand-emerald-hover flex items-center justify-center font-bold text-xs shadow-xs border border-white/80 shrink-0 cursor-pointer overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
                  aria-label="View Profile"
                  title="View Profile"
                >
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (user?.name?.trim()
                      ? user.name.trim()[0]
                      : user?.phone_number
                        ? user.phone_number.slice(-2)
                        : "U"
                    ).toUpperCase()
                  )}
                </button>
                {hasIncompleteOnboarding && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white ring-1 ring-amber-600/20"
                    title="Complete profile setup"
                    aria-label="Incomplete profile setup"
                  />
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => signIn({ returnTo: window.location.pathname })}
                className="nav-cta-btn h-8 px-3.5 inline-flex items-center justify-center bg-brand-emerald hover:bg-brand-emerald-hover active:bg-brand-emerald-hover text-white text-xs font-semibold rounded-full border-0 cursor-pointer shadow-xs transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
              >
                Log in
              </button>
            )}
          </div>
        </header>

        {/* Mobile Navigation Dropdown Menu (Solid opaque card) */}
        {isMobileMenuOpen && (
          <div
            id="mobile-navigation-menu"
            role="region"
            aria-label="Mobile Navigation"
            className="consumer-mobile-menu absolute top-[calc(100%+8px)] inset-x-3.5 max-w-[440px] mx-auto bg-white border border-brand-border rounded-2xl p-4 shadow-2xl flex flex-col gap-3.5 z-50 pointer-events-auto animate-in fade-in slide-in-from-top-2 duration-200"
          >
            <nav aria-label="Mobile Menu Links" className="flex flex-col gap-1">
              {NAV_ITEMS.map((tab) => {
                const isActive = activePage === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-colors border-0 cursor-pointer min-h-[44px] flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald ${
                      isActive
                        ? "text-brand-emerald-hover bg-brand-emerald-subtle font-bold border border-brand-emerald-border/60"
                        : "text-brand-obsidian font-semibold bg-transparent hover:bg-brand-subtle hover:text-brand-emerald-hover active:bg-brand-subtle"
                    }`}
                    onClick={() => handleNavClick(tab.path || tab.id)}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <div className="flex flex-col gap-2 pt-2 border-t border-brand-border">
              {isSignedIn ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/profile");
                    }}
                    className="w-full min-h-[44px] py-2.5 px-4 flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand-emerald hover:bg-brand-emerald-hover active:bg-brand-emerald-hover rounded-full shadow-xs transition-colors border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
                  >
                    <User className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      signOut();
                    }}
                    className="w-full min-h-[44px] py-2.5 px-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 active:bg-rose-100 rounded-full transition-colors border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Sign out</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    signIn({ returnTo: window.location.pathname });
                  }}
                  className="w-full min-h-[44px] py-2.5 text-center text-sm font-bold text-white bg-brand-emerald hover:bg-brand-emerald-hover active:bg-brand-emerald-hover rounded-full shadow-xs transition-colors border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
                >
                  Log in
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
